import { differenceInSeconds } from 'date-fns';
import { memoAll } from '../../../CodesForFoundation';
import { clamp } from '../../../CodesThatsGottaGoSomewhereElse';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { component, ComponentTuple, ObjectManager, property } from '../../../concepts/objects';
import { DateType } from '../../../concepts/objects/DateType';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Enum } from '../../../platform/Enum';
import { round } from '../../../platform/geom/Math';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { Figure, Physical, PotentialInteraction, RelativeTo, TransformScope } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Textable } from '../../canvas/entities/Textable';
import { TextAlignment } from '../../canvas/entities/TextConventions';
import { GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { Commentable } from '../annotations/Commentable';
import { CommentCountAttr, CommentOpenAttr } from '../annotations/CommentAssociations';
import { commentsOpen } from '../annotations/CommentCommands';
import { Tag } from '../annotations/Tag';
import { tagsToggleEdit } from '../annotations/TaggingCommands';
import { TagSupport } from '../annotations/TagSupport';
import { TaskRecordAttr } from '../assignment/TaskAssocations';
import { Audited } from '../auditing/Audited';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { Concealable, IsActuallyConcealed, IsConcealedHintVisible } from '../conceal/Concealable';
import { flip } from '../conceal/ConcealCommands';
import { ConnectTarget } from '../connector/ConnectTarget';
import { Handwriting, Handwritten } from '../handwriting/Handwritten';
import { Pingable } from '../presentation/Pingable';
import { Votable } from '../voting/Votable';
import { VoteCountAttr } from '../voting/Vote';
import { votesOpen } from '../voting/VotingCommands';
import { Colors } from './../../../ui';
import { IndexCardContentField } from './IndexCardContentField';
import { IndexCardLayout } from './IndexCardLayout';
import { indexCardPropertyLayout } from './IndexCardPropertyLayout';
import { IndexCardTitleField } from './IndexCardTitleField';

const invocation = defineCommand(
    'indexCardInvoke',
    'IndexCard Invoke',
    (r, [id, hint]: [string, string?]) => {
        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        if (hint === 'comments') {
            dispatch(commentsOpen, [id]);
        }
        else if (hint === 'votes') {
            dispatch(votesOpen, [id]);
        }
        else if (hint === 'flip' && IsConcealedHintVisible.get(objects.get(id, IndexCard))) {
            dispatch(flip, [[id]]);
        }
        else {
            const tag = objects.getMaybe(hint, Tag);
            if (!!tag) {
                dispatch(tagsToggleEdit, [tag.id, true]);
            }
            else if (hint.startsWith('check')) {
                const card = objects.get(id, IndexCard);
                const line = parseInt(hint.split(':')[1], 10);
                objects.update(() => {
                    const lines = card.content.split(/\n/g);
                    if (lines[line].startsWith('[ ]')) {
                        lines[line] = lines[line].replace(/^\[\s]/, '[x]');
                    }
                    else {
                        lines[line] = lines[line].replace(/^\[.\]/, '[ ]');
                    }
                    card.content = lines.join('\n');
                });
            }
        }
    },
    [],
);

const mutation = defineCommand(
    'indexCardMutate',
    'IndexCard Mutate',
    (r, [id]: [string]) => {
        const ic = r.call(ObjectManager).get(id, IndexCard);
        const isNew = differenceInSeconds(new Date(), ic.created) < 1;
        // If the card was created less than a second ago it's new.  If so, default to the title field.  This is basically a hack to prevent
        // the "plop" from adding a new index card selecting the body instead of the title.
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field', isNew ? 'title' : ''));
    },
    [],
);

const indexCardDef = {
    name: 'IndexCard',
    implements: [
        Physical,
        AutoBringToFront,
        AutoLayoutSubject,
        Audited,
        Concealable,
        ConnectTarget,
        GraffitiTarget,
        Handwritten,
        Pingable,
        Commentable,
        Votable,
        TagSupport,
        Textable
    ],
    invocation,
    mutation,
    propertyLayout: indexCardPropertyLayout,
};

@component(indexCardDef)
export class IndexCard extends RectangularPhysical implements AutoBringToFront, Audited, Concealable, TagSupport, Textable {

    @property({ type: 'number', default: 400 })
    public width!: number;

    @property({ type: 'number', default: 300 })
    public height!: number;

    @property({ type: 'string', oldName: 'title|value' })
    public title!: string;

    @property({ type: 'number', default: Enum.default(TextAlignment), style: true })
    public titleTextAlignH!: TextAlignment;

    @property({ type: 'string', oldName: 'content|value' })
    public content!: string;

    @property({ type: 'number', default: Enum.default(TextAlignment), style: true })
    public contentTextAlignH!: TextAlignment;

    @property({ type: 'string', default: Colors.negative, style: true })
    public color!: string;

    @property({ type: 'string' })
    public userId!: string;

    @property({ type: DateType })
    public created!: Date;

    @property({ type: 'boolean' })
    public concealed!: boolean;

    public get titleFontFamily() {
        return this.contentFontFamily;
    }

    @memoUsing('userId', IsActuallyConcealed)
    public get contentFontFamily() {

        if (IsActuallyConcealed.get(this)) {
            return 'redacted_scriptregular';
        }
        else {
            return Handwriting.resolve(this);
        }
    }

    @memoUsing(
        'children', 'width', 'height', 'content', 'contentFontFamily', 'contentTextAlignH', 'contentTextAlignV',
        CommentCountAttr, CommentOpenAttr, TaskRecordAttr, VoteCountAttr,
    )
    public get layout() {
        return IndexCardLayout(this);
    }

    @memoUsing('layout')
    public get uiFields() {
        return [
            IndexCardTitleField(this),
            IndexCardContentField(this),
        ];
    }

    public get minHeight() {
        return 300;
    }

    @memoAll()
    public queryTransformHints(singular?: boolean) {
        return Object.freeze({
            scaleX: false,
            scaleY: !!singular,
            rotate: false,
            translate: true,
            visible: !singular,
        });
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const tlt = this.modelLocalTransform();
        from = tlt.apply(from);
        to = tlt.apply(to);

        const [a, b, c] = scope.type === 'scale' ? this.jiggleForPotentialMirror(from, to) : to;

        // prev
        const py = from[0].y;
        const pw = Point.vector(from[0], from[1]).length();
        const ph = Point.vector(from[1], from[2]).length();

        // width & height vectors
        const wv = Point.vector(a, b);
        const hv = Point.vector(b, c);

        // Calcualte new center point
        const nc = Rect.fromPoints(to).center();

        // Making use of the TransformScope information, if we can tell that the transform is a scale only upon a single Sticky, we should
        // prevent the sticky from moving so the resize action doesn't feel too strange.  To do this, restrict to cases where x movement
        // is within a single resize range and the width actually changes.
        if (scope.type === 'scale' && scope.items.length === 1) {

            const y = py + clamp(a.y - py, 25);
            const h = Math.max(ph + clamp(hv.length() - ph, 25), this.minHeight);

            if (this.height !== h) {
                this.x = nc.x - (400 / 2);
                this.y = y;
                this.r = 0;
                this.width = pw;
                this.height = h;
            }
        }
        else {
            this.x = round(nc.x - (pw / 2), 4);
            this.y = round(nc.y - (ph / 2), 4);
            this.r = round(0, 4);
            this.width = round(pw, 4);
            this.height = round(ph, 4);
        }

        return this;
    }

    public area(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds.inflate([0, 18]).offset([0, -18])));
    }

    public test(mpt: Point): boolean {
        const lpt = this.toRelative(mpt);
        if (this.localBounds.contains(lpt)) return true;
        if (this.localBounds.pointAt(.5, 0).distanceTo(lpt) <= 18) return true;
        return false;
    }

    public testInteraction({ type, mpt }: PotentialInteraction, _force?: boolean, _selected?: boolean): [boolean, string] {
        const lpt = this.toRelative(mpt);
        if (type === 'invoke') {
            for (const [id, area] of Object.entries(this.layout.tags)) {
                if (area.contains(lpt)) return [true, id];
            }
            // for (const [id, area] of Object.entries(this.layout.checkboxes)) {
            //     if (area.contains(lpt)) return [true, id];
            // }
            if (this.localBounds.pointAt(.5, 0).distanceTo(lpt) <= 18) return [true, 'flip'];
        }
        if (this.localBounds.contains(lpt)) return [type !== 'invoke', 'default'];
        return [false, 'none'];
    }

    public toShallowTuple(): ComponentTuple {
        const ct = super.toShallowTuple();
        // If supposed to be concealed, erase content from tuple
        if (IsActuallyConcealed.get(this)) {
            ct[2].title = '';
            ct[2].content = '';
        }
        return ct;
    }

    public toEmptyTuple(to?: Physical | RelativeTo): ComponentTuple {
        const ct = super.toShallowTuple(to);
        ct[2].title = '';
        ct[2].content = '';
        return ct;
    }

    public toText(): string {
        return [this.title, this.content].filter(x => !!x).join(':\n');
    }

    public toTextArray(): string[] {
        return [this.title, this.content];
    }

    public toHumanString(): string {
        return `IndexCard ("${(this.title || this.content).slice(0, 20)}")`;
    }
}

